import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
    Settings, Bot, GitBranch, Sliders, Loader2, Save,
    ArrowRight, Mail, Users, Briefcase, Sparkles, Circle,
    Info, ChevronRight, Shield, Plus, Trash2, GripVertical,
    Edit3, X, Check, Copy,
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
    { key: 'autonomy', label: 'Agent Autonomy', icon: Bot },
    { key: 'pipelines', label: 'Pipeline Templates', icon: GitBranch },
    { key: 'general', label: 'General', icon: Sliders },
];

const AUTONOMY_CATEGORIES = [
    {
        key: 'pipeline',
        label: 'Pipeline Progression',
        icon: ArrowRight,
        description: 'Control how agents move applicants through pipeline stages',
        fields: [
            { key: 'auto_advance_screening_pass', label: 'Auto-advance on screening pass', description: 'Automatically move applicants to the next stage when they pass screening' },
            { key: 'auto_advance_interview_complete', label: 'Auto-advance after interview', description: 'Automatically progress applicants after an interview is completed' },
            { key: 'auto_reject_low_fit', label: 'Auto-reject low-fit applicants', description: 'Automatically reject applicants below the screening threshold' },
        ],
    },
    {
        key: 'comms',
        label: 'Communication',
        icon: Mail,
        description: 'Control automated emails and notifications to applicants',
        fields: [
            { key: 'auto_send_application_receipt', label: 'Send application receipt', description: 'Automatically confirm receipt of new applications' },
            { key: 'auto_send_stage_updates', label: 'Send stage update emails', description: 'Notify applicants when they move to a new pipeline stage' },
            { key: 'auto_send_followup_nudges', label: 'Send follow-up nudges', description: 'Automatically nudge applicants who haven\'t responded' },
            { key: 'auto_send_rejection', label: 'Send rejection emails', description: 'Automatically send rejection notifications' },
        ],
    },
    {
        key: 'matching',
        label: 'Matching & Sourcing',
        icon: Users,
        description: 'Control how agents match talent to open roles',
        fields: [
            { key: 'auto_match_new_jobs', label: 'Auto-match new jobs', description: 'Automatically find matching candidates when a new job is posted' },
            { key: 'auto_activate_matched_talent', label: 'Auto-activate matched talent', description: 'Automatically add matched candidates to the job pipeline' },
            { key: 'auto_suggest_alternative_roles', label: 'Suggest alternative roles', description: 'Suggest other open roles to applicants who aren\'t a fit' },
        ],
    },
    {
        key: 'crm',
        label: 'Job Orders (CRM Handoff)',
        icon: Briefcase,
        description: 'Control automatic job creation from CRM deals',
        fields: [
            { key: 'auto_create_job_from_deal', label: 'Auto-create from deal', description: 'Automatically create job orders when CRM deals reach a stage' },
        ],
    },
    {
        key: 'ai_content',
        label: 'AI Content Generation',
        icon: Sparkles,
        description: 'Control AI-generated content for job postings and interviews',
        fields: [
            { key: 'auto_generate_job_description', label: 'Generate job descriptions', description: 'Use AI to draft job descriptions from deal context' },
            { key: 'auto_generate_matching_criteria', label: 'Generate matching criteria', description: 'Use AI to define ideal candidate criteria' },
            { key: 'auto_generate_interview_questions', label: 'Generate interview questions', description: 'Use AI to create role-specific interview questions' },
        ],
    },
];

const THRESHOLD_FIELDS = [
    { key: 'screening_pass_threshold', label: 'Screening Pass Threshold', description: 'Minimum score (0-100) to pass automated screening', unit: '%' },
    { key: 'match_score_minimum', label: 'Match Score Minimum', description: 'Minimum match score (0-100) to consider a candidate', unit: '%' },
    { key: 'match_score_auto_shortlist', label: 'Auto-Shortlist Threshold', description: 'Match score (0-100) to automatically shortlist a candidate', unit: '%' },
];

const MODE_OPTIONS = [
    { value: 'auto', label: 'Auto', color: 'bg-emerald-500', hoverColor: 'hover:bg-emerald-600', textColor: 'text-white' },
    { value: 'suggest', label: 'Suggest', color: 'bg-amber-500', hoverColor: 'hover:bg-amber-600', textColor: 'text-white' },
    { value: 'disabled', label: 'Disabled', color: 'bg-stone-300', hoverColor: 'hover:bg-stone-400', textColor: 'text-stone-700' },
];

function ModeToggle({ value, onChange }) {
    return (
        <div className="flex gap-1 rounded-lg bg-stone-100 p-1">
            {MODE_OPTIONS.map(opt => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        onClick={() => onChange(opt.value)}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                            active
                                ? `${opt.color} ${opt.textColor} shadow-sm`
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

export default function ATSSettings() {
    const { getAuthHeader } = useAuth();
    const [tab, setTab] = useState('autonomy');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(null);
    const [autonomy, setAutonomy] = useState({});
    const [templates, setTemplates] = useState([]);
    const [dirty, setDirty] = useState(false);

    const loadConfig = useCallback(async () => {
        try {
            const [configRes, templateRes] = await Promise.all([
                axios.get(`${API}/ats/config`, { headers: getAuthHeader() }),
                axios.get(`${API}/ats/pipeline/templates`, { headers: getAuthHeader() }),
            ]);
            setConfig(configRes.data);
            setAutonomy(configRes.data?.agent_autonomy || {});
            setTemplates(templateRes.data?.data || []);
        } catch {
            toast.error('Failed to load ATS settings');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { loadConfig(); }, [loadConfig]);

    const handleAutonomyChange = (key, value) => {
        setAutonomy(prev => ({ ...prev, [key]: value }));
        setDirty(true);
    };

    const handleThresholdChange = (key, value) => {
        const num = Math.min(100, Math.max(0, parseInt(value) || 0));
        setAutonomy(prev => ({ ...prev, [key]: num }));
        setDirty(true);
    };

    const saveAutonomy = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/ats/agents/autonomy`, { agent_autonomy: autonomy }, { headers: getAuthHeader() });
            toast.success('Agent autonomy settings saved');
            setDirty(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const saveGeneral = async (updates) => {
        setSaving(true);
        try {
            await axios.put(`${API}/ats/config`, updates, { headers: getAuthHeader() });
            setConfig(prev => ({ ...prev, ...updates }));
            toast.success('Settings saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="p-2 bg-stone-100 rounded-xl">
                    <Settings className="w-6 h-6 text-stone-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-stone-900 font-[Manrope]">ATS Settings</h1>
                    <p className="text-sm text-stone-500">Configure pipeline templates and agent autonomy</p>
                </div>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 border-b border-stone-200">
                {TABS.map(t => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                active
                                    ? 'border-stone-900 text-stone-900'
                                    : 'border-transparent text-stone-500 hover:text-stone-700'
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Tab content */}
            {tab === 'autonomy' && (
                <AutonomyTab
                    autonomy={autonomy}
                    onFieldChange={handleAutonomyChange}
                    onThresholdChange={handleThresholdChange}
                    onSave={saveAutonomy}
                    saving={saving}
                    dirty={dirty}
                />
            )}
            {tab === 'pipelines' && <PipelinesTab templates={templates} getAuthHeader={getAuthHeader} onRefresh={loadConfig} />}
            {tab === 'general' && (
                <GeneralTab
                    config={config}
                    templates={templates}
                    onSave={saveGeneral}
                    saving={saving}
                />
            )}
        </div>
    );
}

/* ─── Agent Autonomy Tab ─── */

function AutonomyTab({ autonomy, onFieldChange, onThresholdChange, onSave, saving, dirty }) {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-stone-500">
                    <Shield className="w-4 h-4" />
                    <span>Configure how much autonomy AI agents have across different areas</span>
                </div>
                <Button onClick={onSave} disabled={saving || !dirty} className="gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                </Button>
            </div>

            {/* Mode legend */}
            <div className="flex items-center gap-4 px-4 py-3 rounded-xl bg-stone-50 border border-stone-200 text-xs text-stone-600">
                <span className="font-medium text-stone-700">Modes:</span>
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Auto — agent acts immediately
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Suggest — agent proposes, human approves
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-stone-300" /> Disabled — agent will not act
                </span>
            </div>

            {/* Category sections */}
            {AUTONOMY_CATEGORIES.map(cat => {
                const CatIcon = cat.icon;
                return (
                    <div key={cat.key} className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                        <div className="flex items-center gap-3 px-5 py-4 bg-stone-50 border-b border-stone-200">
                            <CatIcon className="w-5 h-5 text-stone-600" />
                            <div>
                                <h3 className="text-sm font-semibold text-stone-800 font-[Manrope]">{cat.label}</h3>
                                <p className="text-xs text-stone-500">{cat.description}</p>
                            </div>
                        </div>
                        <div className="divide-y divide-stone-100">
                            {cat.fields.map(field => (
                                <div key={field.key} className="flex items-center justify-between px-5 py-3.5">
                                    <div className="flex-1 min-w-0 pr-4">
                                        <p className="text-sm font-medium text-stone-700">{field.label}</p>
                                        <p className="text-xs text-stone-500 mt-0.5">{field.description}</p>
                                    </div>
                                    <ModeToggle
                                        value={autonomy[field.key] || 'disabled'}
                                        onChange={(v) => onFieldChange(field.key, v)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}

            {/* Thresholds */}
            <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-4 bg-stone-50 border-b border-stone-200">
                    <Sliders className="w-5 h-5 text-stone-600" />
                    <div>
                        <h3 className="text-sm font-semibold text-stone-800 font-[Manrope]">Thresholds</h3>
                        <p className="text-xs text-stone-500">Numeric thresholds used by autonomous agents</p>
                    </div>
                </div>
                <div className="divide-y divide-stone-100">
                    {THRESHOLD_FIELDS.map(field => (
                        <div key={field.key} className="flex items-center justify-between px-5 py-4">
                            <div className="flex-1 min-w-0 pr-4">
                                <p className="text-sm font-medium text-stone-700">{field.label}</p>
                                <p className="text-xs text-stone-500 mt-0.5">{field.description}</p>
                            </div>
                            <div className="flex items-center gap-3 w-48">
                                <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={autonomy[field.key] ?? 50}
                                    onChange={(e) => onThresholdChange(field.key, e.target.value)}
                                    className="flex-1 accent-stone-600"
                                />
                                <div className="flex items-center gap-1 w-14 text-right">
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={autonomy[field.key] ?? 50}
                                        onChange={(e) => onThresholdChange(field.key, e.target.value)}
                                        className="w-12 text-sm text-right border border-stone-200 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-stone-400"
                                    />
                                    <span className="text-xs text-stone-400">{field.unit}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ─── Pipeline Templates Tab ─── */

const STAGE_COLORS = [
    '#94a3b8', '#60a5fa', '#a78bfa', '#818cf8', '#c084fc',
    '#f59e0b', '#f97316', '#fb923c', '#2dd4bf', '#34d399',
    '#22c55e', '#ef4444', '#ec4899', '#8b5cf6', '#06b6d4',
];

function SortableStageRow({ stage, index, onUpdate, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.key });
    const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined };
    const [editing, setEditing] = useState(false);
    const [editLabel, setEditLabel] = useState(stage.label);

    const handleSaveLabel = () => {
        if (editLabel.trim()) {
            onUpdate(index, { ...stage, label: editLabel.trim() });
        }
        setEditing(false);
    };

    return (
        <div ref={setNodeRef} style={style}
            className={`flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border transition-shadow ${
                isDragging ? 'shadow-lg border-blue-300 opacity-90' : 'border-stone-200'
            }`}>
            <div {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing p-0.5 -ml-1 rounded hover:bg-stone-100">
                <GripVertical className="w-4 h-4 text-stone-300" />
            </div>

            {/* Color picker */}
            <input type="color" value={stage.color || '#94a3b8'}
                onChange={e => onUpdate(index, { ...stage, color: e.target.value })}
                className="w-5 h-5 rounded cursor-pointer border-0 p-0" title="Stage color" />

            {/* Label */}
            {editing ? (
                <div className="flex items-center gap-1 flex-1">
                    <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveLabel(); if (e.key === 'Escape') setEditing(false); }}
                        className="flex-1 px-2 py-0.5 text-sm border border-stone-300 rounded focus:outline-none focus:ring-1 focus:ring-stone-400"
                        autoFocus />
                    <button onClick={handleSaveLabel} className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded">
                        <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditing(false)} className="p-0.5 text-stone-400 hover:bg-stone-100 rounded">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <span className="flex-1 text-sm text-stone-700 cursor-pointer hover:text-stone-900"
                    onClick={() => { setEditLabel(stage.label); setEditing(true); }}>
                    {stage.label}
                </span>
            )}

            {/* Key badge */}
            <span className="text-[10px] text-stone-400 font-mono bg-stone-50 px-1.5 py-0.5 rounded hidden sm:inline">
                {stage.key}
            </span>

            {/* Terminal toggle */}
            {stage.is_terminal ? (
                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                    stage.terminal_type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                }`}>
                    {stage.terminal_type === 'positive' ? 'Hired' : 'Rejected'}
                </span>
            ) : (
                <span className="text-[10px] text-stone-300">#{index + 1}</span>
            )}

            {/* Actions */}
            {!editing && (
                <div className="flex items-center gap-0.5">
                    <button onClick={() => { setEditLabel(stage.label); setEditing(true); }}
                        className="p-1 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-600" title="Edit label">
                        <Edit3 className="w-3 h-3" />
                    </button>
                    {!stage.is_terminal && (
                        <button onClick={() => onRemove(index)}
                            className="p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-500" title="Remove stage">
                            <Trash2 className="w-3 h-3" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function PipelinesTab({ templates, getAuthHeader, onRefresh }) {
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [stages, setStages] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [newStageName, setNewStageName] = useState('');

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const startEdit = (tpl) => {
        setEditingTemplate(tpl.template_id);
        setTemplateName(tpl.name);
        setIsDefault(tpl.is_default);
        setStages([...(tpl.stages || [])].sort((a, b) => a.order - b.order));
    };

    const cancelEdit = () => {
        setEditingTemplate(null);
        setStages([]);
        setTemplateName('');
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setStages(prev => {
            const oldIndex = prev.findIndex(s => s.key === active.id);
            const newIndex = prev.findIndex(s => s.key === over.id);
            const moved = arrayMove(prev, oldIndex, newIndex);
            return moved.map((s, i) => ({ ...s, order: i }));
        });
    };

    const handleUpdateStage = (index, updated) => {
        setStages(prev => prev.map((s, i) => i === index ? updated : s));
    };

    const handleRemoveStage = (index) => {
        setStages(prev => {
            const filtered = prev.filter((_, i) => i !== index);
            return filtered.map((s, i) => ({ ...s, order: i }));
        });
    };

    const handleAddStage = () => {
        if (!newStageName.trim()) return;
        const key = newStageName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
        if (stages.some(s => s.key === key)) return toast.error('Stage key already exists');
        // Insert before terminal stages
        const terminalIndex = stages.findIndex(s => s.is_terminal);
        const insertAt = terminalIndex >= 0 ? terminalIndex : stages.length;
        const color = STAGE_COLORS[insertAt % STAGE_COLORS.length];
        const newStage = { key, label: newStageName.trim(), order: insertAt, color, is_terminal: false, terminal_type: null };
        const updated = [...stages];
        updated.splice(insertAt, 0, newStage);
        setStages(updated.map((s, i) => ({ ...s, order: i })));
        setNewStageName('');
    };

    const handleSave = async () => {
        if (!templateName.trim()) return toast.error('Template name is required');
        if (stages.length < 2) return toast.error('At least 2 stages required');
        setSaving(true);
        try {
            const payload = {
                name: templateName,
                is_default: isDefault,
                stages: stages.map((s, i) => ({
                    key: s.key,
                    label: s.label,
                    order: i,
                    color: s.color || '#94a3b8',
                    is_terminal: s.is_terminal || false,
                    terminal_type: s.terminal_type || null,
                })),
            };
            if (editingTemplate) {
                await axios.put(`${API}/ats/pipeline-templates/${editingTemplate}`, payload, { headers: getAuthHeader() });
                toast.success('Template updated');
            } else {
                await axios.post(`${API}/ats/pipeline-templates`, payload, { headers: getAuthHeader() });
                toast.success('Template created');
            }
            cancelEdit();
            setShowCreate(false);
            onRefresh?.();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save template'); }
        finally { setSaving(false); }
    };

    const handleDuplicate = (tpl) => {
        setEditingTemplate(null);
        setShowCreate(true);
        setTemplateName(`${tpl.name} (Copy)`);
        setIsDefault(false);
        setStages([...(tpl.stages || [])].sort((a, b) => a.order - b.order));
    };

    const handleCreateNew = () => {
        setShowCreate(true);
        setEditingTemplate(null);
        setTemplateName('New Pipeline');
        setIsDefault(false);
        setStages([
            { key: 'applied', label: 'Applied', order: 0, color: '#94a3b8', is_terminal: false, terminal_type: null },
            { key: 'hired', label: 'Hired', order: 1, color: '#22c55e', is_terminal: true, terminal_type: 'positive' },
            { key: 'unsuccessful', label: 'Unsuccessful', order: 2, color: '#ef4444', is_terminal: true, terminal_type: 'negative' },
        ]);
    };

    // Editing/creating view
    if (editingTemplate || showCreate) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <button onClick={() => { cancelEdit(); setShowCreate(false); }}
                        className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700">
                        <X className="w-4 h-4" /> Cancel
                    </button>
                    <Button onClick={handleSave} disabled={saving} className="bg-stone-900 text-white hover:bg-stone-800">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                        {editingTemplate ? 'Save Changes' : 'Create Template'}
                    </Button>
                </div>

                {/* Template name + default */}
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Template Name</label>
                            <input value={templateName} onChange={e => setTemplateName(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer mt-5">
                            <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)}
                                className="rounded border-stone-300 text-stone-900 focus:ring-stone-900" />
                            <span className="text-sm text-stone-600">Default template</span>
                        </label>
                    </div>
                </div>

                {/* Stage list */}
                <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-stone-700">Pipeline Stages</h4>
                        <span className="text-xs text-stone-400">Drag to reorder</span>
                    </div>

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={stages.map(s => s.key)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-1.5">
                                {stages.map((stage, idx) => (
                                    <SortableStageRow key={stage.key} stage={stage} index={idx}
                                        onUpdate={handleUpdateStage} onRemove={handleRemoveStage} />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>

                    {/* Add stage */}
                    <div className="flex items-center gap-2 pt-2 border-t border-stone-100 mt-3">
                        <input value={newStageName} onChange={e => setNewStageName(e.target.value)}
                            placeholder="New stage name..."
                            onKeyDown={e => e.key === 'Enter' && handleAddStage()}
                            className="flex-1 px-3 py-2 text-sm border border-dashed border-stone-300 rounded-lg focus:outline-none focus:border-stone-400" />
                        <Button onClick={handleAddStage} variant="outline" size="sm" disabled={!newStageName.trim()}>
                            <Plus className="w-4 h-4 mr-1" /> Add Stage
                        </Button>
                    </div>
                    <p className="text-[10px] text-stone-400 mt-1">
                        Terminal stages (Hired/Unsuccessful) cannot be removed. New stages are inserted before terminal stages.
                    </p>
                </div>

                {/* Preview */}
                <div className="bg-stone-50 rounded-xl border border-stone-200 p-4">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Pipeline Preview</h4>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {stages.map((stage, idx) => (
                            <React.Fragment key={stage.key}>
                                {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-stone-300 flex-shrink-0" />}
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-stone-200 bg-white text-xs">
                                    <Circle className="w-2.5 h-2.5 flex-shrink-0" fill={stage.color} stroke={stage.color} />
                                    <span className="text-stone-700">{stage.label}</span>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // List view
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-stone-500">
                    <Info className="w-4 h-4" />
                    <span>Pipeline templates define the stages applicants move through for each job.</span>
                </div>
                <Button onClick={handleCreateNew} size="sm" className="bg-stone-900 text-white hover:bg-stone-800">
                    <Plus className="w-4 h-4 mr-1" /> New Template
                </Button>
            </div>

            {!templates.length ? (
                <div className="text-center py-12 text-stone-500">
                    <GitBranch className="w-8 h-8 mx-auto mb-3 text-stone-300" />
                    <p className="text-sm">No pipeline templates configured</p>
                    <Button onClick={handleCreateNew} variant="outline" size="sm" className="mt-3">
                        <Plus className="w-4 h-4 mr-1" /> Create First Template
                    </Button>
                </div>
            ) : (
                templates.map(tpl => (
                    <div key={tpl.template_id} className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-4 bg-stone-50 border-b border-stone-200">
                            <div className="flex items-center gap-3">
                                <GitBranch className="w-5 h-5 text-stone-600" />
                                <h3 className="text-sm font-semibold text-stone-800 font-[Manrope]">{tpl.name}</h3>
                                {tpl.is_default && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Default</span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-stone-400 mr-2">{tpl.stages?.length || 0} stages</span>
                                <button onClick={() => handleDuplicate(tpl)}
                                    className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600" title="Duplicate">
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                                <Button onClick={() => startEdit(tpl)} variant="outline" size="sm">
                                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                            </div>
                        </div>
                        <div className="px-5 py-4">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                {(tpl.stages || [])
                                    .sort((a, b) => a.order - b.order)
                                    .map((stage, idx) => (
                                        <React.Fragment key={stage.key}>
                                            {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-stone-300 flex-shrink-0" />}
                                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-stone-200 bg-stone-50 text-xs">
                                                <Circle className="w-2.5 h-2.5 flex-shrink-0" fill={stage.color || '#a8a29e'} stroke={stage.color || '#a8a29e'} />
                                                <span className="text-stone-700">{stage.label}</span>
                                                {stage.is_terminal && (
                                                    <span className={`px-1 py-0.5 text-[9px] font-medium rounded ${
                                                        stage.terminal_type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                                                    }`}>{stage.terminal_type === 'positive' ? 'Hired' : 'End'}</span>
                                                )}
                                            </div>
                                        </React.Fragment>
                                    ))}
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

/* ─── General Tab ─── */

function GeneralTab({ config, templates, onSave, saving }) {
    const [cvParser, setCvParser] = useState(config?.cv_parser_engine || 'gpt-4o');
    const [defaultTemplate, setDefaultTemplate] = useState(config?.default_pipeline_template_id || '');
    const [localDirty, setLocalDirty] = useState(false);

    const handleSave = () => {
        onSave({
            cv_parser_engine: cvParser,
            default_pipeline_template_id: defaultTemplate || null,
        });
        setLocalDirty(false);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <p className="text-sm text-stone-500">General ATS configuration options</p>
                <Button onClick={handleSave} disabled={saving || !localDirty} className="gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                </Button>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100">
                {/* CV Parser Engine */}
                <div className="px-5 py-5">
                    <label className="block text-sm font-medium text-stone-700 mb-1">CV Parser Engine</label>
                    <p className="text-xs text-stone-500 mb-3">Select the engine used to parse and extract data from uploaded CVs</p>
                    <select
                        value={cvParser}
                        onChange={(e) => { setCvParser(e.target.value); setLocalDirty(true); }}
                        className="w-full max-w-xs border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400 bg-white"
                    >
                        <option value="gpt-4o">GPT-4o (Recommended)</option>
                        <option value="open_source">Open Source</option>
                    </select>
                </div>

                {/* Default Pipeline Template */}
                <div className="px-5 py-5">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Default Pipeline Template</label>
                    <p className="text-xs text-stone-500 mb-3">New job orders will use this template by default</p>
                    <select
                        value={defaultTemplate}
                        onChange={(e) => { setDefaultTemplate(e.target.value); setLocalDirty(true); }}
                        className="w-full max-w-xs border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400 bg-white"
                    >
                        <option value="">No default</option>
                        {templates.map(tpl => (
                            <option key={tpl.template_id} value={tpl.template_id}>
                                {tpl.name}{tpl.is_default ? ' (current default)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {config?.updated_at && (
                <p className="text-xs text-stone-400">
                    Last updated: {new Date(config.updated_at).toLocaleString()}
                </p>
            )}
        </div>
    );
}
